/** @jsxImportSource preact */
import { useEffect, useRef, useState } from "preact/hooks";

import type { ResolvedWidgetConfig } from "./config";
import type {
  Conversation,
  ConversationState,
} from "./conversation/conversation";
import { WIDGET_KEYFRAMES } from "./ui/animations";
import { leavesPage } from "./ui/navigation";
import { createWidgetView } from "./ui/view-state";
import { MobileDrawer } from "./ui/drawer/mobile-drawer";
import { ConversationPanel } from "./ui/conversation-panel";
import {
  constrainPanelSize,
  getDefaultPanelSize,
  readPanelSize,
  type PanelSize,
} from "./ui/panel-resize";
import { useCharacterReaction } from "./ui/use-character-reaction";
import { useLauncherChrome } from "./ui/use-launcher-chrome";
import type { ProactivePrompt } from "./proactive/proactive";
import { useVoiceController } from "./voice/use-voice";
import type { VoiceViewState } from "./voice/use-voice";

export interface WidgetRootProps {
  config: ResolvedWidgetConfig;
  conversation: Conversation;
  state: ConversationState;
  proactive?: ProactivePrompt;
  voice?: VoiceViewState;
}

/** 聊天窗顶边离视口顶部至少这么远。 */
const PANEL_TOP_GAP_PX = 80;
/** 点了跳转却一直没换走（店面脚本拦下了跳转），这么久后角色醒回来。 */
const LEAVING_RESET_MS = 3000;

/**
 * 挂件根组件：负责开合、桌面自适应尺寸与手动偏好、入口位置、移动端判断，其余交给 WidgetPanel。
 * 没有消息时摆问候语和建议问题，问候语来自商家后台的 welcomeMessage。
 */
export function WidgetRoot({
  config,
  conversation,
  state,
  proactive,
}: WidgetRootProps) {
  const { open } = state;
  const voice = useVoiceController(
    config.voiceGatewayUrl,
    config.voiceMode,
    conversation,
  );
  const voiceView = config.voiceGatewayUrl === undefined ? undefined : voice;
  const chrome = useLauncherChrome(config);
  const {
    mobile,
    viewport,
    obstruction,
    side,
    launcherBottom,
    draggable,
    launcherDrag,
    horizontalSpacing,
    bottom,
    placed,
  } = chrome;
  const reaction = useCharacterReaction(state);
  // 挂载时聊天窗就开着，只可能是换页、刷新接着上一页：首帧摆在原位，不重播展开（设计稿 12a，GON-176）。
  // 买家收起之后再点开，就是正常的展开。
  const restoring = useRef(open);
  if (!open) restoring.current = false;
  const entrance = restoring.current ? "restore" : "open";
  const [view] = useState(() => createWidgetView(config.shopDomain));
  // 买家点了会换页的链接、页面还没换走：最新回复的角色合眼打盹（设计稿 12a）。
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    // 挂在 window 冒泡阶段：店面脚本自己接管的链接（购物车抽屉之类）那时已经 preventDefault 了。
    const click = (event: MouseEvent) => {
      if (leavesPage(event)) setLeaving(true);
    };
    window.addEventListener("click", click);
    return () => {
      window.removeEventListener("click", click);
    };
  }, []);
  useEffect(() => {
    if (!leaving) return;
    const reset = () => {
      setLeaving(false);
    };
    const timer = setTimeout(reset, LEAVING_RESET_MS);
    // 从往返缓存回来时页面原样恢复，角色还在打盹。
    window.addEventListener("pageshow", reset);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("pageshow", reset);
    };
  }, [leaving]);
  const sizeKey = `gonovus:panel-size:${config.shopDomain}`;
  const [preferredSize, setPreferredSize] = useState(() =>
    readPanelSize(sizeKey),
  );
  const available = {
    width: Math.max(0, viewport.width - horizontalSpacing - 16),
    height: Math.max(0, viewport.height - bottom - PANEL_TOP_GAP_PX),
  };
  const size = constrainPanelSize(
    preferredSize ?? getDefaultPanelSize(viewport),
    available,
  );
  const resize = (next: PanelSize | undefined) => {
    const bounded = next && constrainPanelSize(next, available);
    setPreferredSize(bounded);
    try {
      if (bounded) sessionStorage.setItem(sizeKey, JSON.stringify(bounded));
      else sessionStorage.removeItem(sizeKey);
    } catch {
      // 禁用存储时保留当前页面内的尺寸。
    }
  };
  // 入口拖得太高时聊天窗往下挪，保持顶边留白，不压缩买家设的尺寸。
  const panelOffset = Math.max(
    0,
    size.height + PANEL_TOP_GAP_PX - (viewport.height - launcherBottom),
  );
  if (state.disabled) return null;

  return (
    <div
      class="gonovus-widget"
      style={{
        position: "fixed",
        zIndex: 1000,
        ...(mobile
          ? {
              top: viewport.top,
              left: viewport.left,
              // 零宽外壳让露出的店面自然接收触摸；祖先禁用指针会干扰 iOS 原生滚动。
              width: 0,
              "--gn-viewport-width": `${String(viewport.width)}px`,
              height: viewport.drawerHeight,
            }
          : {
              bottom: `max(${String(launcherBottom)}px, env(safe-area-inset-bottom))`,
              [side]: horizontalSpacing,
            }),
      }}
    >
      {/* 关键帧跟着组件走：放 shadowRoot 直下会多出一个子节点，卸载时也要另外清。 */}
      <style>{WIDGET_KEYFRAMES}</style>
      {mobile ? (
        <>
          <MobileDrawer
            config={config}
            conversation={conversation}
            state={state}
            reaction={reaction}
            height={viewport.drawerHeight}
            keyboardOpen={viewport.keyboardOpen}
            entrance={entrance}
            leaving={open && leaving}
            view={view}
            obstruction={obstruction}
            voice={voiceView}
          />
          <div
            class="gn-mobile-entry"
            data-open={open}
            aria-hidden={open || undefined}
            inert={open}
            style={{
              position: "fixed",
              pointerEvents: open ? "none" : "auto",
              bottom: `max(${String(launcherBottom)}px, env(safe-area-inset-bottom))`,
              [side]: horizontalSpacing,
            }}
          >
            <ConversationPanel
              config={placed}
              conversation={conversation}
              state={{ ...state, open: false }}
              reaction={reaction}
              proactive={open ? undefined : proactive}
              launcherDrag={draggable ? launcherDrag : undefined}
              width={`${String(size.width)}px`}
              voice={voiceView}
            />
          </div>
        </>
      ) : (
        <ConversationPanel
          config={placed}
          conversation={conversation}
          height={`${String(size.height)}px`}
          onResize={resize}
          launcherDrag={draggable ? launcherDrag : undefined}
          panelOffset={panelOffset}
          mobile={mobile}
          entrance={entrance}
          leaving={open && leaving}
          view={view}
          state={state}
          reaction={reaction}
          proactive={proactive}
          voice={voiceView}
          width={`${String(size.width)}px`}
        />
      )}
    </div>
  );
}

/**
 * 后台 Playground 的根组件：挂件占满页面给的盒子。
 * 店面那套避让购买控件、跟随 visualViewport 的计算在后台没有对应物，所以不跑。
 */
export function EmbeddedRoot({ config, conversation, state }: WidgetRootProps) {
  const reaction = useCharacterReaction(state);
  const voice = useVoiceController(
    config.voiceGatewayUrl,
    config.voiceMode,
    conversation,
  );
  const voiceView = config.voiceGatewayUrl === undefined ? undefined : voice;
  return (
    <div class="gonovus-widget" style={{ height: "100%", width: "100%" }}>
      <style>{WIDGET_KEYFRAMES}</style>
      <ConversationPanel
        config={config}
        conversation={conversation}
        embedded
        state={state}
        reaction={reaction}
        voice={voiceView}
      />
    </div>
  );
}
